import React, { useState, FC, useEffect } from "react"
import Bar from "../../../components/Bar"
import { connect } from "react-redux"
import { IStoreState } from "../../../reducers"
import { ArrayState } from "../../../reducers/types"
import { SET_ARRAY } from "../../../reducers/array"
import MergeSortTracker, { IMergeSortTrace } from "./MergeSortTracker"
import Chart from "../../../components/Chart"

interface IMergeSortProps {
    initialData: number[]
    startSort: boolean
    isSorted: boolean
    onComplete: () => void
    setArray: (array: number[]) => void
}

const copy = (from: number[], to: number[]) => {
    for (let i = 0; i < from.length; i++) {
        to[i] = from[i]
    }
}

const TopDownMerge = (arr: number[], begin: number, middle: number, end: number, tracker: MergeSortTracker): void => {
    let i = begin,
        j = middle
    const tempArray = []
    while (i < j && j <= end) {
        tracker.traceCompare([...arr], [i, j])
        if (arr[i] <= arr[j]) {
            tempArray.push(arr[i])
            i++
        } else {
            tracker.traceSwap([...arr], [i, j])

            // Note: used array shifting to show more realistic visualization ;)
            const temp = [...arr.slice(0, i), arr[j], ...arr.slice(i, j), ...arr.slice(j + 1)]
            copy(temp, arr)

            tracker.trace([...arr], [-1, -1], [-1, -1])

            tempArray.push(arr[j])
            j++
        }
    }
}

const TopDownSplitMergeSort = (A: number[], begin: number, end: number, tracker: MergeSortTracker): void => {
    const size = end - begin + 1

    if (size <= 1) {
        return
    }

    const middle: number = begin + Math.floor(size / 2) - 1

    tracker.traceBoundary([...A], [begin, middle])
    TopDownSplitMergeSort(A, begin, middle, tracker)

    tracker.traceBoundary([...A], [middle + 1, end])
    TopDownSplitMergeSort(A, middle + 1, end, tracker)

    tracker.traceBoundary([...A], [begin, end])
    TopDownMerge(A, begin, middle + 1, end, tracker)
}

const mergeSortAlgo = (data: number[]): MergeSortTracker => {
    const arr = [...data]
    const tracker = new MergeSortTracker([...arr])

    TopDownSplitMergeSort(arr, 0, arr.length - 1, tracker)

    tracker.trackSorted([...arr])
    return tracker
}

const MergeSort: FC<IMergeSortProps> = ({
    initialData,
    startSort,
    isSorted,
    onComplete,
    setArray,
}: IMergeSortProps) => {
    const [compare, setCompare] = useState<[number, number]>([-1, -1])
    const [swapped, setSwapped] = useState<[number, number]>([-1, -1])
    const [bounderies, setBoundries] = useState<[number, number]>([-1, -1])

    const data = initialData
    const numWidth = 98 / data.length
    const width = `${numWidth}%`
    const color = numWidth < 20 ? "white" : "transparent"

    function handleDispatch({ traces, speed }: { traces: IMergeSortTrace[]; speed: number }) {
        if (!traces.length) {
            onComplete()
            return
        }

        const trace = traces.shift()
        if (!trace) {
            onComplete()
            return
        }
        setArray(trace.data)
        setCompare(trace.comparedElements)
        setSwapped(trace.swappedElements)
        setBoundries(trace.boundaryElements)

        setTimeout(() => {
            handleDispatch({ traces, speed })
        }, speed)
    }

    function onClickHandler() {
        const tracker = mergeSortAlgo(data)

        handleDispatch({ traces: tracker.traces.splice(0), speed: 0 })
    }

    useEffect(() => {
        if (startSort) {
            onClickHandler()
        }
        // eslint-disable-next-line
    }, [startSort])

    return (
        <Chart>
            {data.map((number: number, index: number) => {
                const backgroundColor = swapped.includes(index)
                    ? "rgba(219, 57, 57, 0.8)"
                    : compare.includes(index)
                    ? "rgba(78, 216, 96, 0.8)"
                    : bounderies.includes(index)
                    ? "pink"
                    : isSorted
                    ? "#22AF4B"
                    : "rgba(66, 134, 244, 0.8)"

                return (
                    <Bar
                        key={index}
                        height={`${(number / data.length) * 100}%`}
                        width={width}
                        bgColor={backgroundColor}
                        color={color}
                    />
                )
            })}
        </Chart>
    )
}

const mapStateToProps = (state: IStoreState) => {
    return {
        isSorted: state.arrayState === ArrayState.Sorted,
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        setArray: (array: number[]) => dispatch({ type: SET_ARRAY, payload: array }),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MergeSort)
